/**
 * The card a repay is going back to — read only.
 *
 * On a card repay nothing here is a choice. The money returns to the card that
 * paid, through the terminal that took it, against the original charge. TB's
 * Remittance Advice shows the same three facts greyed out (card, value date,
 * expiry), and they serve one purpose: letting the agent confirm they are
 * refunding the RIGHT charge before the money moves, since the refund cannot be
 * undone once Pelecard accepts it.
 *
 * Everything is read from the receipt the repay was opened from. Nothing is
 * fetched and nothing is editable, so this cannot change what gets refunded — the
 * server resolves the charge again from `repayForDocumentNumber` and refuses if
 * they disagree.
 */

import { FC, memo, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import { Voucher } from '../../../../api/vouchers/types';
import { getDateByFormat } from '../../../../utils/dateUtils';
import request from '../../../../api/request';

type Props = {
    documentGenerationData: Partial<Voucher> | null;
};

/** One greyed fact. Disabled rather than plain text so it reads as a form field. */
const ReadOnlyField: FC<{ label: string; value: string }> = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
        <TextField
            label={label}
            value={value || '—'}
            variant="standard"
            fullWidth
            disabled
            InputProps={{ readOnly: true }}
        />
    </Grid>
);

const RepayCardDetails: FC<Props> = ({ documentGenerationData }) => {
    const info: any = documentGenerationData?.info || {};
    const card: any = info.paymentMethodObject || {};

    // Expiry arrives as Pelecard's MMYY ("0732"). Shown as MM/YY rather than
    // reformatted into a date — it is a card expiry, not a day.
    const expiry = String(card.expDate || '');
    const expiryDisplay = /^\d{4}$/.test(expiry) ? `${expiry.slice(0, 2)}/${expiry.slice(2)}` : expiry;

    // The terminal comes from any of three places, and all three are real.
    //
    // The documents query selects paymentsTerminalId/Number as plain columns on
    // the ROW, not inside `info` — json_build_object there is at Postgres'
    // 100-argument ceiling. And the two receipt flows differ: Issue Documents
    // embeds the whole terminal row under paymentMethodObject, tour registration
    // keeps only the name there.
    const row: any = documentGenerationData || {};
    const terminalName = card.paymentsTerminal?.name || card.paymentsTerminalName || '';
    const terminalNumber =
        card.paymentsTerminal?.terminalNumber ||
        row.paymentsTerminalNumber ||
        info.paymentsTerminalNumber ||
        '';

    const payments = Number(card.installmentPlan?.totalPayments) || Number(info.totalPayments) || 0;

    // A receipt copied from a TravelBooster file (Import TF from TB) records money TB's
    // own card provider took — not Pelecard's to give back, so the server refuses a card
    // repay of it. Asked only when there is no terminal, the case that blocks the repay.
    const receiptId = (documentGenerationData as any)?._id;
    const [fromTb, setFromTb] = useState(false);
    useEffect(() => {
        if (terminalNumber || !receiptId) return;
        let cancelled = false;
        request
            .get(`/vouchers-from-tb/${receiptId}`)
            .then((res) => {
                if (!cancelled) setFromTb(!!res?.data?.fromTb);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [terminalNumber, receiptId]);

    return (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                    Refunding to the card of receipt {documentGenerationData?.number ?? ''} — these
                    details come from the original charge and cannot be changed.
                </Typography>
            </Grid>

            <ReadOnlyField label="Credit Card" value={card.cardNo || ''} />
            <ReadOnlyField label="Expire Date" value={expiryDisplay} />
            <ReadOnlyField
                label="Value date"
                value={card.valueDate ? getDateByFormat(card.valueDate, 'dd/MMM/yy') : ''}
            />
            <ReadOnlyField label="Credit Company" value={card.creditCompanyName || ''} />
            <ReadOnlyField
                label="Terminal"
                value={[terminalName, terminalNumber].filter(Boolean).join(' · ')}
            />
            <ReadOnlyField label="Approval No." value={card.approvalCode || ''} />

            {/* Said out loud rather than left to fail on submit. The CREDIT_CARD
                validation schema requires a terminal, and this view renders no
                terminal field — so without a resolved one the dialog would just
                refuse to submit with the error attached to a control that is not
                on screen, which looks like a dead button. */}
            {!terminalNumber && (
                <Grid item xs={12}>
                    <Typography variant="caption" color="error">
                        {fromTb
                            ? 'This payment was taken in TravelBooster, through its card provider — it cannot be refunded by card from Taytan. Return the money in TravelBooster’s provider or by bank transfer, then record this repay as a bank transfer.'
                            : 'No clearing terminal is recorded on this receipt, so the refund cannot be routed back. It has to be issued in Pelecard’s back office.'}
                    </Typography>
                </Grid>
            )}

            {/* Only when the original was an instalment deal. Pelecard credits it
                over the SAME number of payments, so the customer sees the money
                come back in instalments too — worth saying before it happens. */}
            {payments > 1 && (
                <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                        The original charge was taken in {payments} payments, so the refund is
                        credited over {payments} payments as well.
                    </Typography>
                </Grid>
            )}
        </Grid>
    );
};

export default memo(RepayCardDetails);
